import { useCallback, useEffect, useRef, useState } from 'react';
import Axios from '../api/axios';
import * as glb from '../globals';
import { subscribeNotifications } from '../services/notificationService';
import { getNotificationSocketState, subscribeNotificationSocket } from '../utils/notificationSocket';
import { publishBandCount } from '../utils/browserBandNotification';

const FALLBACK_POLL_MS = 5 * 60_000;
const EMPTY_OZET = { yeni_atama_sayisi: 0, atama_bos_sayisi: 0, admin_mi: false };

function normalizeOzet(data) {
  return {
    yeni_atama_sayisi: Number(data?.yeni_atama_sayisi) || 0,
    atama_bos_sayisi: Number(data?.atama_bos_sayisi) || 0,
    admin_mi: !!data?.admin_mi,
  };
}

export function useCariAtamaBildirim() {
  const [ozet, setOzet] = useState(EMPTY_OZET);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchOzet = useCallback(async () => {
    const kulid = glb.default?.KULLANICI?.KUL_REF;
    if (!kulid) return EMPTY_OZET;
    try {
      setLoading(true);
      const res = await Axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER}cari_atama_bildirim/ozet`,
      );
      const data = res.data || EMPTY_OZET;
      const next = normalizeOzet(data);
      if (mountedRef.current) setOzet(next);
      publishBandCount('atama-yeni', next.yeni_atama_sayisi, {
        title: 'Atama',
        body: next.yeni_atama_sayisi === 1
          ? 'Size 1 yeni cari atandı'
          : `Size ${next.yeni_atama_sayisi} yeni cari atandı`,
      });
      if (next.admin_mi) {
        publishBandCount('atama-bos', next.atama_bos_sayisi, {
          title: 'Atamasız',
          body: next.atama_bos_sayisi === 1
            ? '1 carinin ataması boş'
            : `${next.atama_bos_sayisi} carinin ataması boş`,
        });
      }
      return data;
    } catch (_) {
      return EMPTY_OZET;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const applyOzet = useCallback((data) => {
    if (!mountedRef.current || !data) return;
    const next = normalizeOzet(data);
    setOzet(next);
    publishBandCount('atama-yeni', next.yeni_atama_sayisi, {
      title: 'Atama',
      body: next.yeni_atama_sayisi === 1
        ? 'Size 1 yeni cari atandı'
        : `Size ${next.yeni_atama_sayisi} yeni cari atandı`,
    });
    if (next.admin_mi) {
      publishBandCount('atama-bos', next.atama_bos_sayisi, {
        title: 'Atamasız',
        body: next.atama_bos_sayisi === 1
          ? '1 carinin ataması boş'
          : `${next.atama_bos_sayisi} carinin ataması boş`,
      });
    }
  }, []);

  const markOkundu = useCallback(async (tip, carRefs = null) => {
    const kulid = glb.default?.KULLANICI?.KUL_REF;
    if (!kulid) return;
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER}cari_atama_bildirim/okundu`,
        { kulid, tip, car_refs: carRefs },
      );
      applyOzet(res.data || EMPTY_OZET);
    } catch (_) { /* ignore */ }
  }, [applyOzet]);

  useEffect(() => {
    mountedRef.current = true;
    fetchOzet();

    const onNotification = (n) => {
      const type = String(n?.type || '').toUpperCase();
      if (type === 'CARI_ASSIGNMENT_BADGE' && n?.data?.ozet) {
        applyOzet(n.data.ozet);
        return;
      }
      if (type === 'CARI_ASSIGNMENT_BADGE' && n?.data?.action === 'refresh') {
        fetchOzet();
        return;
      }
      if (type === 'CARI_ASSIGNED' || type === 'CARI_UNASSIGNED') {
        fetchOzet();
      }
    };

    const unsub = subscribeNotifications(onNotification);
    const unsubTransport = subscribeNotificationSocket((msg) => {
      if (msg?.type === 'WS_OPEN') fetchOzet();
    });

    const onWsEvent = (evt) => onNotification(evt?.detail || {});
    window.addEventListener('cari-atama-bildirim-ws', onWsEvent);

    const timer = setInterval(() => {
      if (getNotificationSocketState() !== 'open') fetchOzet();
    }, FALLBACK_POLL_MS);

    const onFocus = () => {
      if (getNotificationSocketState() !== 'open') fetchOzet();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible' && getNotificationSocketState() !== 'open') {
        fetchOzet();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('cari-atama-bildirim-refresh', fetchOzet);

    return () => {
      mountedRef.current = false;
      unsub();
      unsubTransport();
      window.removeEventListener('cari-atama-bildirim-ws', onWsEvent);
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('cari-atama-bildirim-refresh', fetchOzet);
    };
  }, [fetchOzet, applyOzet]);

  return { ozet, loading, refresh: fetchOzet, markOkundu };
}
